// @ts-nocheck
import React, { useState, useMemo } from 'react';
import {
  LuRefreshCw,
  LuChevronDown,
  LuChevronUp,
  LuClock,
  LuHash,
  LuCode2,
  LuLoader,
  LuInbox,
  LuTimer,
  LuCalendarCheck2,
  LuZap,
  LuX,
  LuFilter,
  LuCheckCircle2,
  LuXCircle,
  LuAlertCircle,
  LuCircleDashed,
  LuActivity,
  LuUser,
  LuSend,
  LuBadgeCheck,
  LuUpload,
  LuHeart,
  LuRotateCcw,
  LuTerminal,
  LuCpu,
  LuListChecks,
  LuArrowRight,
} from 'react-icons/lu';
import { Popover, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CodeViewer } from '@/components/organism/CodeViewer';
import { TimelineItem } from '@/components/elements/TimelineItem';
import {
  ExpandableCard,
  ExpandablePanel,
} from '@/components/elements/ExpandablePanel';
import { DetailsList } from '@/components/elements/DetailsList';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import {
  APP_BG,
  APP_BORDER,
  APP_TEXT,
  APP_SEMANTIC,
  APP_VDI,
  APP_HEX,
} from '@/constant/theme/colors.constant';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { IconBadge } from '@/components/atoms/IconBadge';
import PillCell, {
  ACTION_LOG_STATUS_PILL_MAP,
} from '@/components/organism/StatusPills/PillCell';
import AppButton from '@/components/atoms/AppButton';
import { IActionLog } from '../smartCooler.interface';

// ─── Status config ────────────────────────────────────────────────────────────
// badge/pulse/dot fields removed — PillCell + ACTION_LOG_STATUS_PILL_MAP owns them.
// iconColor is hex for inline style (valid per colors.constant.ts convention).

function statusCfg(status) {
  const s = (status ?? '').toUpperCase();
  if (s === 'COMPLETED')
    return {
      glow: 'shadow-emerald-100 dark:shadow-emerald-900/20',
      ring: 'ring-emerald-300/40 dark:ring-emerald-700/30',
      iconColor: APP_HEX.success,
      label: 'Completed',
      statIcon: (
        <LuCheckCircle2 className={`w-3.5 h-3.5 ${APP_TEXT.success}`} />
      ),
    };
  if (s === 'FAILED')
    return {
      glow: 'shadow-red-100 dark:shadow-red-900/20',
      ring: 'ring-red-300/40 dark:ring-red-700/30',
      iconColor: APP_HEX.danger,
      label: 'Failed',
      statIcon: <LuXCircle className={`w-3.5 h-3.5 ${APP_TEXT.danger}`} />,
    };
  if (s === 'ACCEPTED' || s === 'IN_PROGRESS')
    return {
      glow: 'shadow-indigo-100 dark:shadow-indigo-900/20',
      ring: 'ring-indigo-300/40 dark:ring-indigo-700/30',
      iconColor: APP_VDI.inProgress,
      label: s === 'ACCEPTED' ? 'Accepted' : 'In Progress',
      statIcon: <LuZap className={`w-3.5 h-3.5 ${APP_TEXT.indigo}`} />,
    };
  if (s === 'PENDING')
    return {
      glow: 'shadow-amber-100 dark:shadow-amber-900/20',
      ring: 'ring-amber-300/40 dark:ring-amber-700/30',
      iconColor: APP_HEX.warning,
      label: 'Pending',
      statIcon: (
        <LuCircleDashed className={`w-3.5 h-3.5 ${APP_TEXT.warning}`} />
      ),
    };
  return {
    glow: '',
    ring: 'ring-gray-200/50 dark:ring-gray-700/20',
    iconColor: APP_HEX.gray400,
    label: status ?? 'Unknown',
    statIcon: <LuAlertCircle className={`w-3.5 h-3.5 ${APP_TEXT.muted}`} />,
  };
}

// ─── Action type config ───────────────────────────────────────────────────────

const ACTION_TYPE_CFG = {
  UPLOAD_LOGS: {
    label: 'Upload Logs',
    icon: <LuUpload className="w-5 h-5" />,
    color: APP_HEX.violetMid,
  },
  GET_HEALTH: {
    label: 'Get Health',
    icon: <LuHeart className="w-5 h-5" />,
    color: APP_HEX.teal,
  },
  RESTART_DEVICE: {
    label: 'Restart Device',
    icon: <LuRotateCcw className="w-5 h-5" />,
    color: APP_HEX.danger,
  },
};

function getActionCfg(type) {
  return (
    ACTION_TYPE_CFG[type] ?? {
      label: type?.replace(/_/g, ' ') ?? 'Unknown',
      icon: <LuTerminal className="w-5 h-5" />,
      color: APP_HEX.info,
    }
  );
}

// ─── Execution time ───────────────────────────────────────────────────────────

function execTime(log) {
  const s = (log.status ?? '').toUpperCase();
  if (!['COMPLETED', 'FAILED'].includes(s)) return null;
  const endIso = log.completedOn ?? log.updatedOn;
  if (!endIso || !log.createdOn) return null;
  const ms = new Date(endIso).getTime() - new Date(log.createdOn).getTime();
  if (isNaN(ms) || ms < 0) return null;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function shortId(id) {
  return id ? id.slice(0, 8) : '—';
}
function hasContent(obj) {
  return obj != null && typeof obj === 'object' && Object.keys(obj).length > 0;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonRow = () => {
  return (
    <div
      className={`rounded-2xl border ${APP_BORDER.default} ${APP_BG.base} p-4 animate-pulse`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-xl shrink-0 ${APP_BG.muted}`} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className={`h-4 w-28 rounded-lg ${APP_BG.muted}`} />
            <div className={`h-4 w-16 rounded-full ${APP_BG.muted}`} />
          </div>
          <div className="flex gap-3">
            <div className={`h-3 w-24 rounded ${APP_BG.elevated}`} />
            <div className={`h-3 w-32 rounded ${APP_BG.elevated}`} />
          </div>
        </div>
        <div className={`h-5 w-20 rounded-full ${APP_BG.muted}`} />
      </div>
    </div>
  );
};

// ─── Action Card (uses ExpandableCard) ────────────────────────────────────────

const ActionCard = ({ log, expanded, onToggle }) => {
  const st = statusCfg(log.status);
  const ac = getActionCfg(log.actionType);
  const et = execTime(log);
  const showPayload = hasContent(log.payload);
  const showResponse = hasContent(log.response);

  const TL_INDIGO = APP_VDI.inProgress;
  const TL_EMERALD = APP_HEX.success;
  const TL_INACTIVE = '#cbd5e1';
  const TL_CONNECTOR = APP_HEX.slate400;

  const timelineSteps = [
    {
      label: 'Created',
      time: log.createdOn,
      icon: <LuClock className="w-3 h-3 text-white" />,
      color: TL_INDIGO,
      done: true,
    },
    {
      label: 'Sent',
      time: log.sentOn,
      icon: <LuSend className="w-3 h-3 text-white" />,
      color: log.sentOn ? TL_INDIGO : TL_INACTIVE,
      done: !!log.sentOn,
    },
    {
      label: 'Acknowledged',
      time: log.acknowledgedOn,
      icon: <LuBadgeCheck className="w-3 h-3 text-white" />,
      color: log.acknowledgedOn ? TL_INDIGO : TL_INACTIVE,
      done: !!log.acknowledgedOn,
    },
    {
      label: 'Completed',
      time: log.completedOn,
      icon: <LuCheckCircle2 className="w-3 h-3 text-white" />,
      color: log.completedOn ? TL_EMERALD : TL_INACTIVE,
      done: !!log.completedOn,
    },
  ];
  const currentStepIdx = timelineSteps.reduce(
    (acc, step, i) => (step.done ? i : acc),
    0,
  );

  return (
    <ExpandableCard
      expanded={expanded}
      onToggle={onToggle}
      testId={tid(TID.ACTION_LOGS_EXPAND_ROW(log.actionId))}
      accentColor={st.iconColor}
      expandedRing={`ring-2 ${st.ring}`}
      expandedShadow={`shadow-lg ${st.glow}`}
      showChevron={false}
      headerClassName="pl-5 pr-4 py-3.5"
      header={
        <div className="flex items-center gap-3.5 w-full">
          {/* Action type icon */}
          <IconBadge
            icon={ac.icon}
            size="md"
            variant="light"
            color={ac.color}
          />

          {/* Centre: label + optional message + meta */}
          <div className="flex-1 min-w-0">
            <p
              className={`text-[13px] font-semibold leading-snug ${APP_TEXT.default}`}
            >
              {ac.label}
            </p>
            {log.message && (
              <p
                className={`text-[11px] mt-0.5 leading-snug ${APP_TEXT.muted}`}
              >
                {log.message}
              </p>
            )}
            <div
              className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] ${APP_TEXT.muted}`}
            >
              <span className="inline-flex items-center gap-1 font-mono">
                <LuHash className="w-2.5 h-2.5 opacity-60" />
                {shortId(log.actionId)}
              </span>
              <span className="inline-flex items-center gap-1">
                <LuClock className="w-2.5 h-2.5 opacity-60" />
                {getDateTime(log.createdOn)}
              </span>
              {log.requestedBy && (
                <span className="inline-flex items-center gap-1 font-mono">
                  <LuUser className="w-2.5 h-2.5 opacity-60 shrink-0" />
                  {log.requestedBy.replace(/^auth0\|/, '')}
                </span>
              )}
            </div>
          </div>

          {/* Right: status pill + chevron + meta */}
          <div className="shrink-0 flex flex-col items-end gap-1.5 min-w-[90px]">
            <div className="flex items-center gap-2">
              <PillCell
                value={log.status}
                map={ACTION_LOG_STATUS_PILL_MAP}
                dot
                size="text-[11px]"
              />
              <div
                className={[
                  'w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm',
                  expanded
                    ? 'bg-indigo-500 text-white'
                    : `${APP_BG.muted} ${APP_TEXT.soft} group-hover:bg-indigo-100 dark:group-hover:bg-indigo-950/50 group-hover:text-indigo-500`,
                ].join(' ')}
              >
                {expanded ? (
                  <LuChevronUp className="w-3.5 h-3.5" />
                ) : (
                  <LuChevronDown className="w-3.5 h-3.5" />
                )}
              </div>
            </div>
            {et && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-mono tabular-nums font-semibold ${APP_TEXT.soft}`}
              >
                <LuTimer className="w-2.5 h-2.5" />
                {et}
              </span>
            )}
            <span className={`text-[10px] tabular-nums ${APP_TEXT.muted}`}>
              {getDateTimeAgo(log.createdOn)}
            </span>
          </div>
        </div>
      }
    >
      {/* ── Expanded panel ── */}
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${APP_BG.base}`}>
        {/* ── Timeline column ── */}
        <div
          className={`px-6 py-5 border-b lg:border-b-0 lg:border-r ${APP_BORDER.soft}`}
        >
          <p
            className={`text-[10px] uppercase tracking-[0.14em] font-bold mb-5 flex items-center gap-1.5 ${APP_TEXT.subtle}`}
          >
            <LuListChecks className="w-3.5 h-3.5" />
            Timeline
          </p>
          <div className="relative flex flex-col gap-0">
            {timelineSteps.map((step, i) => (
              <TimelineItem
                key={step.label}
                icon={step.icon}
                dotColor={step.done ? step.color : 'transparent'}
                title={step.label}
                value={step.time}
                done={step.done}
                current={i === currentStepIdx}
                last={i === timelineSteps.length - 1}
                connectorColor={
                  step.done && timelineSteps[i + 1]?.done
                    ? step.color
                    : TL_CONNECTOR
                }
                pulseColor={step.color}
              />
            ))}
          </div>
        </div>

        {/* ── Details column ── */}
        <div className="px-5 py-4 flex flex-col gap-3 min-w-0">
          <DetailsList
            title="Details"
            titleIcon={<LuHash className="w-3.5 h-3.5" />}
            items={[
              { label: 'Action ID', value: log.actionId, mono: true },
              { label: 'Cooler ID', value: log.coolerId, mono: true },
              { label: 'Action Type', value: log.actionType, mono: true },
              {
                label: 'Status',
                value: (
                  <PillCell
                    value={log.status}
                    map={ACTION_LOG_STATUS_PILL_MAP}
                    dot
                    size="text-[11px]"
                  />
                ),
              },
              {
                label: 'Requested By',
                value: log.requestedBy?.replace(/^auth0\|/, ''),
                mono: true,
                hidden: !log.requestedBy,
              },
              {
                label: 'Duration',
                value: (
                  <span
                    className={`text-[12px] font-mono font-bold tabular-nums ${APP_TEXT.default}`}
                  >
                    {et}
                  </span>
                ),
                hidden: !et,
              },
            ]}
          />

          {log.message && (
            <div
              className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border ${APP_SEMANTIC.warning.bg} ${APP_SEMANTIC.warning.border}`}
            >
              <LuAlertCircle
                className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${APP_SEMANTIC.warning.text}`}
              />
              <p className={`text-[11px] leading-relaxed ${APP_TEXT.soft}`}>
                {log.message}
              </p>
            </div>
          )}

          {(showPayload || showResponse) && (
            <div className="flex gap-1.5 justify-end mt-auto">
              {showPayload && (
                <Popover className="relative">
                  {({ open: pOpen }) => (
                    <>
                      <Popover.Button
                        onClick={(e) => e.stopPropagation()}
                        className={[
                          'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all duration-150 border',
                          pOpen
                            ? 'bg-violet-100 dark:bg-violet-950/50 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-700/60'
                            : 'bg-violet-50/70 dark:bg-violet-950/20 text-violet-600 dark:text-violet-400 border-violet-200/70 dark:border-violet-800/40 hover:bg-violet-100 dark:hover:bg-violet-950/40 hover:border-violet-300 dark:hover:border-violet-700/60',
                        ].join(' ')}
                      >
                        <LuCode2 className="w-2.5 h-2.5" />
                        Payload
                      </Popover.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1 scale-95"
                        enterTo="opacity-100 translate-y-0 scale-100"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0 scale-100"
                        leaveTo="opacity-0 translate-y-1 scale-95"
                      >
                        <Popover.Panel
                          onClick={(e) => e.stopPropagation()}
                          className={`absolute right-0 bottom-full mb-2 z-50 w-[480px] max-w-[90vw] rounded-2xl shadow-2xl overflow-hidden border ${APP_BG.base} ${APP_BORDER.card}`}
                        >
                          <div className="h-[3px] bg-gradient-to-r from-violet-500 to-purple-600" />
                          <div className="max-h-72 overflow-auto p-3">
                            <CodeViewer
                              content={JSON.stringify(log.payload, null, 2)}
                              language="json"
                              defaultOpen
                            />
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
              )}
              {showResponse && (
                <Popover className="relative">
                  {({ open: pOpen }) => (
                    <>
                      <Popover.Button
                        onClick={(e) => e.stopPropagation()}
                        className={[
                          'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all duration-150 border',
                          pOpen
                            ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700/60'
                            : 'bg-emerald-50/70 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 border-emerald-200/70 dark:border-emerald-800/40 hover:bg-emerald-100 dark:hover:bg-emerald-950/40 hover:border-emerald-300 dark:hover:border-emerald-700/60',
                        ].join(' ')}
                      >
                        <LuCode2 className="w-2.5 h-2.5" />
                        Response
                      </Popover.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1 scale-95"
                        enterTo="opacity-100 translate-y-0 scale-100"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0 scale-100"
                        leaveTo="opacity-0 translate-y-1 scale-95"
                      >
                        <Popover.Panel
                          onClick={(e) => e.stopPropagation()}
                          className={`absolute right-0 bottom-full mb-2 z-50 w-[480px] max-w-[90vw] rounded-2xl shadow-2xl overflow-hidden border ${APP_BG.base} ${APP_BORDER.card}`}
                        >
                          <div className="h-[3px] bg-gradient-to-r from-emerald-500 to-teal-500" />
                          <div className="max-h-72 overflow-auto p-3">
                            <CodeViewer
                              content={JSON.stringify(log.response, null, 2)}
                              language="json"
                              defaultOpen
                            />
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </>
                  )}
                </Popover>
              )}
            </div>
          )}
        </div>
      </div>
    </ExpandableCard>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ViewSCActionLogsTab = ({
  coolerId,
  logs,
  loading,
  loadingMore,
  error,
  totalRecords,
  initialized,
  onInit,
  onLoadMore,
  onRefresh,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  React.useEffect(() => {
    if (!initialized) onInit();
  }, []);

  const hasMore = logs.length < totalRecords;

  const filteredLogs = useMemo(() => {
    if (statusFilter === 'ALL') return logs;
    return logs.filter((l) => (l.status ?? '').toUpperCase() === statusFilter);
  }, [logs, statusFilter]);

  const toolbar = (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3 ${APP_BG.surface}`}
    >
      <div className="flex items-center gap-2.5">
        <IconBadge
          icon={<LuActivity className="w-3.5 h-3.5" />}
          size="sm"
          variant="solid"
        />
        <div>
          <p
            className={`text-[13px] font-bold leading-none ${APP_TEXT.default}`}
          >
            Action Logs
          </p>
          <p className={`text-[11px] mt-1 ${APP_TEXT.muted}`}>
            {logs.length === totalRecords
              ? `${totalRecords} total records`
              : `${logs.length} of ${totalRecords} loaded`}
          </p>
        </div>
      </div>
      <AppButton
        data-testid={tid(TID.ACTION_LOGS_REFRESH_BTN)}
        variant="neutral"
        size="sm"
        icon={<LuRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />}
        onClick={onRefresh}
        disabled={loading}
      >
        Refresh
      </AppButton>
    </div>
  );

  // Filtered-empty state (filter active but no matches)
  if (!loading && filteredLogs.length === 0 && statusFilter !== 'ALL') {
    return (
      <div className="flex flex-col min-h-0">
        <div className={`border-b ${APP_BORDER.soft}`}>{toolbar}</div>
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-8">
          <div
            className={`w-11 h-11 rounded-xl flex items-center justify-center border ${APP_BORDER.default} ${APP_BG.surface}`}
          >
            <LuFilter className={`w-4 h-4 ${APP_TEXT.subtle}`} />
          </div>
          <div className="space-y-1">
            <p className={`text-sm font-semibold ${APP_TEXT.default}`}>
              No matching logs
            </p>
            <p
              className={`text-xs leading-relaxed max-w-[200px] ${APP_TEXT.muted}`}
            >
              Try a different filter or clear the active selection.
            </p>
          </div>
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold border transition-all active:scale-95 ${APP_BORDER.default} ${APP_BG.elevated} ${APP_TEXT.soft} hover:text-indigo-600 dark:hover:text-indigo-400`}
          >
            <LuX className="w-3 h-3" />
            Clear filter
          </button>
        </div>
      </div>
    );
  }

  return (
    <ExpandablePanel
      items={filteredLogs}
      getItemId={(log) => log.actionId}
      renderItem={(log, expanded, onToggle) => (
        <ActionCard log={log} expanded={expanded} onToggle={onToggle} />
      )}
      loading={loading}
      loadingMore={loadingMore}
      skeletonCount={5}
      renderSkeleton={() => <SkeletonRow />}
      error={error}
      errorTitle="Failed to load action logs"
      onRetry={onRefresh}
      emptyIcon={
        <div className="relative">
          <LuInbox className={`w-7 h-7 ${APP_TEXT.subtle}`} />
          <div
            className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ring-2 ${APP_BG.base} bg-indigo-100 dark:bg-indigo-950/60`}
          >
            <LuActivity className="w-3 h-3 text-indigo-500" />
          </div>
        </div>
      }
      emptyTitle="No action logs yet"
      emptyDescription="Actions triggered on this device will appear here with full timeline and payload details."
      emptyAction={
        <AppButton
          variant="neutral"
          size="sm"
          icon={<LuRefreshCw className="w-3 h-3" />}
          onClick={onRefresh}
        >
          Refresh
        </AppButton>
      }
      toolbar={toolbar}
      hasMore={hasMore && statusFilter === 'ALL'}
      totalRecords={totalRecords}
      loadedCount={logs.length}
      onLoadMore={onLoadMore}
      expandedId={expandedId}
      onExpandChange={setExpandedId}
      scrollable
    />
  );
};

export default ViewSCActionLogsTab;
